import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Code2, Search, Filter, BookOpen, ChevronRight, Sun, Moon, 
  User, LogOut, ArrowLeft, Clock, CheckCircle, Award, Trophy, Info, Download, GraduationCap
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { coursesApi, courseRequestsApi } from '../lib/api';
import { toast } from 'sonner';
import CourseDescriptionModal from '../components/CourseDescriptionModal';
import CourseCertificate from '../components/CourseCertificate';

const languages = [
  { value: 'all', label: 'All Courses' },
  { value: 'programming', label: '💻 Programming' },
  { value: 'life_skills', label: '🎯 Life Skills' },
  { value: 'safety', label: '🛡️ Safety' },
  { value: 'financial', label: '💰 Financial' },
  { value: 'career', label: '🚀 Career' },
  { value: 'environment', label: '🌱 Environment' },
  { value: 'python', label: 'Python' },
  { value: 'sql', label: 'SQL' },
  { value: 'c', label: 'C' },
  { value: 'cpp', label: 'C++' },
  { value: 'java', label: 'Java' },
  { value: 'html', label: 'HTML/CSS' },
  { value: 'php', label: 'PHP' },
  { value: 'bash', label: 'Bash' },
  { value: 'shell', label: 'Shell' },
  { value: 'powershell', label: 'PowerShell' }
];

const languageColors = {
  python: 'bg-blue-500',
  sql: 'bg-orange-500',
  c: 'bg-gray-600',
  cpp: 'bg-purple-500',
  java: 'bg-red-500',
  html: 'bg-pink-500',
  php: 'bg-indigo-500',
  bash: 'bg-green-500',
  shell: 'bg-green-500',
  powershell: 'bg-cyan-500'
};

const categoryColors = {
  programming: 'bg-blue-600',
  life_skills: 'bg-green-600',
  safety: 'bg-red-600',
  financial: 'bg-yellow-600',
  career: 'bg-purple-600',
  environment: 'bg-teal-600'
};

export default function CourseBrowser() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [enrollingCourse, setEnrollingCourse] = useState(null);
  const [descriptionModal, setDescriptionModal] = useState({ open: false, courseId: null });
  const [certificateModal, setCertificateModal] = useState({ open: false, data: null });

  const isAdmin = ['super_admin', 'org_admin', 'faculty'].includes(user?.role);

  useEffect(() => {
    fetchData();
  }, [languageFilter]);

  const fetchData = async () => {
    try {
      const filters = {};
      // Handle language and category filters
      const categories = ['programming', 'life_skills', 'safety', 'financial', 'career', 'environment'];
      if (languageFilter !== 'all') {
        if (categories.includes(languageFilter)) {
          filters.category = languageFilter;
        } else {
          filters.language = languageFilter;
        }
      }

      // Apply board/grade filtering for students
      if (!isAdmin && user?.board) {
        filters.board = user.board;
      }
      if (!isAdmin && user?.grade) {
        filters.grade = user.grade;
      }

      const [coursesData, myCoursesData] = await Promise.all([
        coursesApi.getAll(filters),
        courseRequestsApi.getMyCourses()
      ]);

      setCourses(coursesData);
      setMyCourses(myCoursesData);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (courseId) => {
    setEnrollingCourse(courseId);
    try {
      await courseRequestsApi.create(courseId);
      toast.success('Enrolled! You can start learning right away.');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setEnrollingCourse(null);
    }
  };

  const handleDownloadCertificate = async (courseId) => {
    try {
      const certData = await coursesApi.getCertificate(courseId);
      // Ensure course_id is available for the PDF endpoint used inside CourseCertificate.
      setCertificateModal({ open: true, data: { ...certData, course_id: courseId } });
    } catch (error) {
      toast.error('Certificate not available. Please complete the course exam first.');
    }
  };

  const isEnrolled = (courseId) => {
    return myCourses.some(c => c.course_id === courseId);
  };

  const filteredCourses = courses.filter(course => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        course.title.toLowerCase().includes(query) ||
        course.description.toLowerCase().includes(query) ||
        (course.language && course.language.toLowerCase().includes(query)) ||
        (course.category && course.category.toLowerCase().includes(query))
      );
    }
    return true;
  });

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Phase 40.3 — enrollment cap removed. Students can enroll in any number
  // of courses simultaneously and every request is auto-approved.
  const activeCourses = myCourses.filter(c => ['approved', 'in_progress'].includes(c.status)).length;

  return (
    <div className="min-h-screen bg-background">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="gradient-blob w-96 h-96 bg-secondary/20 -top-48 -left-48" />
        <div className="gradient-blob w-64 h-64 bg-primary/20 bottom-0 right-1/4" />
      </div>

      {/* Header */}
      <header className="relative z-10 glass border-b border-border/50 sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Code2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold">CognitiaIndia</span>
            </Link>

            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <User className="w-4 h-4 text-primary" />
                      )}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Life-Skills & Civic missions — animated non-technical hub */}
        <div className="mb-8 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white p-6 shadow-lg" data-testid="nontech-hub-card">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest opacity-80">New · Interactive Missions</p>
              <h2 className="text-2xl font-bold mt-1">Civic Sense — Learn by Living the Scenarios</h2>
              <p className="text-sm mt-2 opacity-90 max-w-2xl">
                14 animated missions with human caricatures, real-life decisions, &ldquo;Words of Wisdom&rdquo; and a scenario-based final quiz. No text-heavy pages — pure interactive learning.
              </p>
            </div>
            <button
              onClick={() => navigate('/mission/civic_sense')}
              data-testid="start-civic-sense-btn"
              className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold px-5 py-2.5 rounded-lg shadow"
            >
              Start Missions →
            </button>
          </div>
        </div>

        {/* Back Button & Title */}
        <div className="mb-8">
          <Button
            variant="ghost"
            className="mb-4"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold mb-2">Browse Courses</h1>
          <p className="text-muted-foreground">
            {!isAdmin && user?.board ? (
              <>Courses personalized for <span className="font-semibold text-primary">{user.board} Class {user.grade}</span>. </>
            ) : null}
            Explore our catalog of courses. Enroll in as many as you like — every enrollment is instant.
            <span className="ml-2 font-medium text-foreground">
              ({activeCourses} active)
            </span>
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11 rounded-lg"
              data-testid="course-search-input"
            />
          </div>
          <Select value={languageFilter} onValueChange={setLanguageFilter}>
            <SelectTrigger className="w-full sm:w-48 h-11 rounded-lg" data-testid="language-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Language" />
            </SelectTrigger>
            <SelectContent>
              {languages.map(lang => (
                <SelectItem key={lang.value} value={lang.value}>{lang.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Courses Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="p-6 rounded-xl bg-card border border-border">
                <div className="skeleton h-6 w-20 mb-4" />
                <div className="skeleton h-6 w-3/4 mb-2" />
                <div className="skeleton h-4 w-full mb-2" />
                <div className="skeleton h-4 w-2/3 mb-6" />
                <div className="skeleton h-10 w-full" />
              </div>
            ))}
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="empty-state py-16 rounded-xl bg-card border border-border">
            <BookOpen className="empty-state-icon w-16 h-16" />
            <h3 className="text-lg font-semibold mb-2">No courses found</h3>
            <p className="text-muted-foreground">Try adjusting your search or filters.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map((course) => {
              const enrolled = isEnrolled(course.course_id);
              const enrollment = myCourses.find(c => c.course_id === course.course_id);
              const isCompleted = enrollment?.status === 'completed';
              const isExamPending = enrollment?.status === 'exam_pending';
              const badgeColor = course.language 
                ? languageColors[course.language] || 'bg-gray-500' 
                : categoryColors[course.category] || 'bg-teal-500';
              
              return (
                <div
                  key={course.course_id}
                  className="rounded-xl bg-card border border-border transition-all duration-300 hover:-translate-y-1 hover:shadow-lg overflow-hidden"
                  data-testid={`course-item-${course.course_id}`}
                >
                  {course.avatar_url && (
                    <div
                      className="relative h-32 bg-gradient-to-br from-indigo-500/20 via-fuchsia-500/10 to-emerald-500/20 flex items-end justify-center overflow-hidden"
                      data-testid={`course-avatar-wrap-${course.course_id}`}
                    >
                      <img
                        src={course.avatar_url}
                        alt={`${course.title} instructor`}
                        className="h-32 w-32 object-cover object-top rounded-full ring-4 ring-background shadow-lg -mb-6 relative z-10"
                        loading="lazy"
                        data-testid={`course-avatar-${course.course_id}`}
                      />
                    </div>
                  )}
                  <div className={`p-6 ${course.avatar_url ? 'pt-10' : ''}`}>
                  <div className="flex items-center justify-between mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${badgeColor}`}>
                      {course.language ? course.language.toUpperCase() : course.category?.replace('_', ' ').toUpperCase() || 'COURSE'}
                    </span>
                    {isCompleted && (
                      <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                        <Trophy className="w-3 h-3 mr-1" />
                        Completed
                      </Badge>
                    )}
                    {isExamPending && (
                      <Badge className="bg-amber-500/20 text-amber-500 border-amber-500/30">
                        <Award className="w-3 h-3 mr-1" />
                        Exam Ready
                      </Badge>
                    )}
                    {!enrolled && (
                      <Badge variant="outline" className="text-xs">
                        {course.difficulty}
                      </Badge>
                    )}
                  </div>

                  <h3 className="text-lg font-semibold mb-2">{course.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {course.description}
                  </p>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      {course.category === 'programming' ? '14 chapters' : '12 chapters'}
                    </div>
                    {course.has_exam && (
                      <div className="flex items-center gap-1">
                        <Award className="w-4 h-4" />
                        Exam included
                      </div>
                    )}
                    {course.grade_levels && course.grade_levels.length > 0 && (
                      <div className="flex items-center gap-1" data-testid={`course-grades-${course.course_id}`}>
                        <GraduationCap className="w-4 h-4" />
                        Class {Math.min(...course.grade_levels)}-{Math.max(...course.grade_levels)}
                      </div>
                    )}
                    <button
                      onClick={() => setDescriptionModal({ open: true, courseId: course.course_id })}
                      className="flex items-center gap-1 text-primary hover:underline"
                      data-testid={`course-description-btn-${course.course_id}`}
                    >
                      <Info className="w-4 h-4" />
                      Details
                    </button>
                  </div>

                  {enrolled ? (
                    <div className="space-y-2">
                      <Button
                        className="w-full rounded-lg"
                        variant={isCompleted ? "outline" : "default"}
                        onClick={() => navigate(`/lab/${course.course_id}`)}
                        data-testid={`continue-course-${course.course_id}`}
                      >
                        {isCompleted ? (
                          <>
                            <Trophy className="w-4 h-4 mr-2 text-green-500" />
                            Review Course
                          </>
                        ) : isExamPending ? (
                          <>
                            <Award className="w-4 h-4 mr-2" />
                            Take Exam
                          </>
                        ) : (
                          <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Continue Learning
                        </>
                      )}
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                      {isCompleted && (
                        <Button
                          className="w-full rounded-lg"
                          variant="secondary"
                          onClick={() => handleDownloadCertificate(course.course_id)}
                          data-testid={`download-certificate-${course.course_id}`}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Certificate
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Button
                      className="w-full btn-primary rounded-lg"
                      onClick={() => handleEnroll(course.course_id)}
                      disabled={enrollingCourse === course.course_id}
                      data-testid={`enroll-course-${course.course_id}`}
                    >
                      {enrollingCourse === course.course_id ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          Enroll Now
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      
      {/* Course Description Modal */}
      <CourseDescriptionModal
        courseId={descriptionModal.courseId}
        isOpen={descriptionModal.open}
        onClose={() => setDescriptionModal({ open: false, courseId: null })}
      />
      
      {/* Certificate Modal */}
      <CourseCertificate
        certificate={certificateModal.data}
        isOpen={certificateModal.open}
        onClose={() => setCertificateModal({ open: false, data: null })}
      />
    </div>
  );
}
